export const SERVER_ACTION_SKEW_EVENT = 'praxis:server-action-skew';
export const FAILED_TO_FIND_SERVER_ACTION = 'Failed to find Server Action';
export const SERVER_ACTION_NOT_FOUND_BODY = 'Server action not found';
export const NEXT_ACTION_NOT_FOUND_HEADER = 'x-nextjs-action-not-found';
export const FETCH_PATCH_FLAG = 'praxisServerActionSkewPatched';
export const PENDING_SKEW_FLAG = 'praxisServerActionSkewPending';

export type ServerActionSkewDetail = {
	actionId: string | null;
	url: string;
	status: number;
	detectedBy: 'header' | 'body';
};

declare global {
	interface WindowEventMap {
		[SERVER_ACTION_SKEW_EVENT]: CustomEvent<ServerActionSkewDetail>;
	}
}

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

type FetchTarget = {
	fetch: typeof fetch;
	[FETCH_PATCH_FLAG]?: boolean;
};

type PraxisWindow = Window & {
	[FETCH_PATCH_FLAG]?: boolean;
	[PENDING_SKEW_FLAG]?: ServerActionSkewDetail;
};

type SkewResponse = Pick<Response, 'status'> & {
	headers?: Pick<Headers, 'get'>;
};

const praxisWindow = (): PraxisWindow | null => {
	if (typeof window === 'undefined') return null;
	return window as PraxisWindow;
};

const headerValue = (headers: unknown, name: string): string | null => {
	if (!headers) return null;
	try {
		return new Headers(headers as ConstructorParameters<typeof Headers>[0]).get(name);
	} catch {
		return null;
	}
};

const responseHeader = (response: SkewResponse, name: string): string | null => {
	try {
		return response.headers?.get(name) ?? null;
	} catch {
		return null;
	}
};

export const getFetchUrl = (input: FetchInput): string => {
	if (typeof input === 'string') return input;
	if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
	if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
	return '';
};

export const getNextActionId = (input: FetchInput, init?: FetchInit): string | null => {
	const fromInit = headerValue(init?.headers, 'Next-Action');
	if (fromInit) return fromInit;
	if (typeof Request !== 'undefined' && input instanceof Request) {
		return input.headers.get('Next-Action');
	}
	return null;
};

export const hasActionNotFoundHeader = (response: SkewResponse): boolean =>
	responseHeader(response, NEXT_ACTION_NOT_FOUND_HEADER) === '1';

export const bodyIndicatesServerActionSkew = (bodyText: string | null | undefined): boolean => {
	if (!bodyText) return false;
	return bodyText.includes(FAILED_TO_FIND_SERVER_ACTION) || bodyText.includes(SERVER_ACTION_NOT_FOUND_BODY);
};

export const shouldReadSkewBody = (response: SkewResponse): boolean => {
	if (hasActionNotFoundHeader(response)) return false;
	const contentType = responseHeader(response, 'content-type') ?? '';
	return contentType.includes('text/plain');
};

export const isServerActionSkew = (
	input: FetchInput,
	init: FetchInit | undefined,
	response: SkewResponse,
	bodyText?: string | null,
): boolean => {
	if (!getNextActionId(input, init)) return false;
	if (hasActionNotFoundHeader(response)) return true;
	return bodyIndicatesServerActionSkew(bodyText);
};

export const markServerActionSkew = (detail: ServerActionSkewDetail): void => {
	const win = praxisWindow();
	if (!win) return;
	win[PENDING_SKEW_FLAG] = detail;
	win.dispatchEvent(new CustomEvent<ServerActionSkewDetail>(SERVER_ACTION_SKEW_EVENT, { detail }));
};

export const peekPendingServerActionSkew = (): ServerActionSkewDetail | null => {
	const win = praxisWindow();
	return win?.[PENDING_SKEW_FLAG] ?? null;
};

export const consumePendingServerActionSkew = (): ServerActionSkewDetail | null => {
	const win = praxisWindow();
	if (!win) return null;
	const pending = win[PENDING_SKEW_FLAG] ?? null;
	delete win[PENDING_SKEW_FLAG];
	return pending;
};

export const reloadPage = (): void => {
	window.location.reload();
};

export const installServerActionSkewFetchPatch = (target: FetchTarget = window as unknown as FetchTarget): void => {
	if (typeof window === 'undefined' || target[FETCH_PATCH_FLAG]) return;

	target[FETCH_PATCH_FLAG] = true;
	const originalFetch = target.fetch.bind(target);

	target.fetch = async (input: FetchInput, init?: FetchInit) => {
		const response = await originalFetch(input, init);
		const actionId = getNextActionId(input, init);
		if (!actionId) return response;

		let bodyText: string | null = null;
		if (shouldReadSkewBody(response)) {
			try {
				bodyText = await response.clone().text();
			} catch {
				bodyText = null;
			}
		}

		if (isServerActionSkew(input, init, response, bodyText)) {
			markServerActionSkew({
				actionId,
				url: getFetchUrl(input),
				status: response.status,
				detectedBy: hasActionNotFoundHeader(response) ? 'header' : 'body',
			});
		}
		return response;
	};
};
